import { registerIndicator, type KLineData } from 'klinecharts'
import type { PlotType } from '@edge/charts-indicators'
import { scaleAlpha } from './color'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'

/** Registered name of the candle-pane overlay variant of the volume indicator. */
export const VOL_OVERLAY_NAME = 'VOL_OVERLAY'

// Default MA period for a freshly-added VOL indicator. Mirrors the catalog's
// `getDefaultCalcParams('VOL')` (periodParam ma1 = 20). Without an explicit
// seed VOL mounts with `[]`, and `regenerateFigures([])` produces ZERO MA
// lines — the "MA 20" the settings drawer advertises then never draws.
export const VOL_DEFAULT_CALC_PARAMS: number[] = [20]

/**
 * In overlay mode the bars share the price pane with the candles, so they
 * render at reduced OPACITY — same hue as the candles, just translucent so they
 * recede behind the price and don't compete with it. In its own pane the
 * indicator keeps the full, opaque theme color.
 */
const OVERLAY_OPACITY = 0.25

/** Fraction of the candle-pane height the overlay volume band occupies (bottom-up). */
const OVERLAY_BAND_RATIO = 0.2

type VolBarStyle = {
  upColor: string
  downColor: string
  noChangeColor: string
  plotType?: PlotType
}

const barColorStyle = ((
  data: { current: { kLineData: KLineData } },
  indicator: { styles?: { bars?: VolBarStyle[] } },
  defaultStyles: { bars: VolBarStyle[] },
) => {
  const kLineData = data.current.kLineData
  // The per-instance override (user's Style-tab color + the baked default
  // opacity) lives on `indicator.styles.bars[0]`; klinecharts hands the THEME
  // default as `defaultStyles` and then spreads this return over the merged
  // style, so reading `defaultStyles` here silently discarded the user's pick.
  // Read the override first, falling back to the theme default only when no
  // override has been materialized yet.
  const bars = indicator?.styles?.bars?.[0] ?? defaultStyles.bars[0]
  // Drawn as a line (the Style tab's Plot type), one color: per-bar rising and
  // falling would cut the line into green and red pieces.
  if (bars.plotType === 'line') return { color: bars.noChangeColor }
  if (kLineData) {
    if (kLineData.close > kLineData.open) return { color: bars.upColor }
    if (kLineData.close < kLineData.open) return { color: bars.downColor }
  }
  return { color: bars.noChangeColor }
}) as never

/**
 * Register the volume indicators. Two variants share the same data model but
 * render very differently, so they are registered under separate names:
 *
 * - `VOL` — the classic sub-pane histogram (own pane, own volume y-axis,
 *   optional MA lines via calcParams). Overrides the built-in klinecharts VOL
 *   so the tooltip reads "Vol: " instead of "VOLUME: ".
 *
 * - `VOL_OVERLAY` — volume as an *overlay* on the candle pane: a self-scaled
 *   band along the bottom. klinecharts folds every figure value of every
 *   indicator in a pane into one shared y-axis range (`YAxisImp.calcRange`),
 *   so this variant exposes NO figures (volume magnitudes would blow out the
 *   price scale) and paints the bars itself via `draw`, scaled to the
 *   visible-range volume max. It also deliberately omits `minValue`/`precision`:
 *   calcRange folds a registered `minValue: 0` into the candle pane's range
 *   (forcing the price axis to start at 0) and a registered `precision: 0`
 *   would round the price-axis labels to integers. Its canvas tooltip is
 *   suppressed — the React legend row shows the live value instead (see
 *   useIndicatorValues).
 */
export const registerVolumeIndicator = registerOnce(() => {
  registerIndicator({
    name: 'VOL',
    shortName: 'Vol',
    series: 'volume',
    shouldOhlc: false,
    shouldFormatBigNumber: true,
    precision: 0,
    minValue: 0,
    figures: [
      {
        key: 'volume',
        title: 'Vol: ',
        type: 'bar',
        baseValue: 0,
        styles: barColorStyle,
      },
    ],
    regenerateFigures: (params: number[]) => {
      // Volume value FIRST in the legend (the primary series), MA(s) SECOND (the
      // optional overlay). MA figures intentionally carry no per-figure `styles`
      // callback: the MA line draws from its instance style (catalog `ma1`
      // defaultColor → soft white, or the user's Style-tab override). A hardcoded
      // callback here would override that instance style, silently killing the
      // color picker AND diverging the drawn line from the legend text. Single
      // source of truth = the catalog default + user override, materialized by
      // buildIndicatorStyles.
      const figures = [
        {
          key: 'volume',
          title: 'Vol: ',
          type: 'bar',
          baseValue: 0,
          styles: barColorStyle,
        },
      ]
      params.forEach((p: number, i: number) => {
        figures.push({ key: `ma${i + 1}`, title: `MA${p}: `, type: 'line' } as never)
      })
      return figures
    },
    calc: (
      dataList: KLineData[],
      indicator: { calcParams: number[]; figures: Array<{ key: string }> },
    ) => {
      const { calcParams: params, figures } = indicator
      const volSums: number[] = []
      return dataList.map((kLineData, i) => {
        const volume = kLineData.volume ?? 0
        const vol: Record<string, number> = { volume }
        params.forEach((p, index) => {
          volSums[index] = (volSums[index] ?? 0) + volume
          if (i >= p - 1) {
            // MA figures start at index 1 — figures[0] is the raw volume bar.
            // Writing to `figures[index]` (index 0 for the default [20]) would
            // overwrite each bar's real volume with its moving average,
            // flattening genuine volume spikes. Offset by 1 to hit `ma1`, `ma2`…
            vol[figures[index + 1].key] = volSums[index] / p
            volSums[index] -= dataList[i - (p - 1)].volume ?? 0
          }
        })
        return vol
      })
    },
  } as IndicatorTemplateParam)

  registerIndicator({
    name: VOL_OVERLAY_NAME,
    shortName: 'Vol',
    // MUST be 'normal', NOT 'volume'. klinecharts' `synchronizeSeriesPrecision`
    // forces every `series: 'volume'` indicator's precision to the volume
    // precision (0). On the candle pane that 0 wins the `Math.min(...)` in
    // `YAxisImp.calcRange`, so the price axis computes its tick step as
    // `10^0 = 1`. For a low-priced ticker whose visible range is under $1
    // (common when zoomed in), klinecharts then treats min≈max and pads the
    // range by ±4, collapsing the candles into a flat line. This variant draws
    // its bars manually and exposes no figures/tooltip, so it never needed the
    // volume series at all — keeping it 'normal' leaves the price precision intact.
    series: 'normal',
    shouldOhlc: false,
    shouldFormatBigNumber: true,
    // Negative zLevel makes klinecharts paint this indicator with
    // 'destination-over', so the band renders BEHIND the candles.
    zLevel: -1,
    // No figures: keeps volume magnitudes out of the candle pane's shared
    // y-axis range. The bars are painted by `draw` below.
    figures: [],
    calc: (dataList: KLineData[]) =>
      dataList.map((kLineData) => ({ volume: kLineData.volume ?? 0 })),
    draw: ((params: {
      ctx: CanvasRenderingContext2D
      kLineDataList: KLineData[]
      visibleRange: { from: number; to: number }
      bounding: { height: number }
      barSpace: { gapBar: number }
      xAxis: { convertToPixel: (dataIndex: number) => number }
      defaultStyles: {
        bars: Array<{ upColor: string; downColor: string; noChangeColor: string }>
      }
    }) => {
      const { ctx, kLineDataList, visibleRange, bounding, barSpace, xAxis, defaultStyles } = params
      const { from, to } = visibleRange

      let maxVolume = 0
      for (let i = from; i < to; i++) {
        const v = kLineDataList[i]?.volume ?? 0
        if (v > maxVolume) maxVolume = v
      }
      // Nothing to scale against — cover the default render and bail.
      if (maxVolume <= 0) return true

      const bandHeight = bounding.height * OVERLAY_BAND_RATIO
      const baseY = bounding.height
      const bars = defaultStyles.bars[0]
      const upColor = scaleAlpha(bars.upColor, OVERLAY_OPACITY)
      const downColor = scaleAlpha(bars.downColor, OVERLAY_OPACITY)
      const noChangeColor = scaleAlpha(bars.noChangeColor, OVERLAY_OPACITY)
      const width = Math.max(barSpace.gapBar, 1)

      ctx.save()
      for (let i = from; i < to; i++) {
        const data = kLineDataList[i]
        if (!data) continue
        const volume = data.volume ?? 0
        if (volume <= 0) continue
        const barHeight = (volume / maxVolume) * bandHeight
        const x = xAxis.convertToPixel(i)
        let color = noChangeColor
        if (data.close > data.open) color = upColor
        else if (data.close < data.open) color = downColor
        ctx.fillStyle = color
        ctx.fillRect(x - width / 2, baseY - barHeight, width, barHeight)
      }
      ctx.restore()

      // Returning true skips klinecharts' default figure rendering.
      return true
    }) as never,
    // The React legend row carries the live "Vol: …" readout in overlay mode;
    // a canvas tooltip would double-print it in the same corner.
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)
})
